/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, RefreshCw, Search, Navigation, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LocationSelector = ({ onLocationChange }) => {
  const [locationName, setLocationName] = useState('Detecting...');
  const [accuracy, setAccuracy] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  const defaultLocation = { name: 'Kurnool, AP' };

  const suggestedCities = ['Tirupati', 'Tiruchanur', 'Renigunta', 'Chandragiri', 'Rajampet', 'Chittoor', 'Nellore'];

  const detectLocation = () => {
    setLoading(true);
    setLocationName('Detecting...');
    if (!navigator.geolocation) {
      setLocationName(defaultLocation.name);
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setAccuracy(accuracy);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14&addressdetails=1`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await response.json();
          const addr = data.address;
          // Try to get the most specific meaningful name
          const city =
            addr.city ||
            addr.town ||
            addr.municipality ||
            addr.district ||
            addr.county ||
            addr.village ||
            addr.suburb ||
            addr.state_district ||
            addr.state ||
            'Unknown Location';
          setLocationName(city);
          if (onLocationChange) onLocationChange(city);
        } catch (error) {
          console.error('Error fetching location name:', error);
          setLocationName(defaultLocation.name);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationName(defaultLocation.name);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    detectLocation();
  }, []);

  // Live search as user types
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&countrycodes=in&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await response.json();
        setSearchResults(data || []);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);

    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  const handleSelectResult = (result) => {
    const addr = result.address;
    const name =
      addr.city ||
      addr.town ||
      addr.municipality ||
      addr.district ||
      addr.county ||
      addr.village ||
      addr.suburb ||
      result.display_name.split(',')[0];
    setLocationName(name);
    if (onLocationChange) onLocationChange(name);
    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownOpen(false);
  };

  const getAccuracyColor = (acc) => {
    if (!acc) return 'bg-gray-400';
    if (acc < 100) return 'bg-green-500';
    if (acc < 500) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
      >
        <MapPin size={16} className="text-blue-600 flex-shrink-0" />
        <span className="text-xs font-bold text-slate-800 truncate max-w-[80px] sm:max-w-[120px]">
          {locationName}
        </span>
      </button>

      <AnimatePresence>
        {isDropdownOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/5"
              onClick={() => {
                setIsDropdownOpen(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-blue-100 z-50 overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-800 font-['Oswald']">Current Location</span>
                  <button
                    onClick={detectLocation}
                    disabled={loading}
                    title="Detect again"
                    className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                  >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {/* Search box */}
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search city/area..."
                    className="w-full pl-9 pr-4 py-2 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
                    autoComplete="off"
                  />
                  {searchLoading ? (
                    <Loader size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin" />
                  ) : (
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  )}
                </div>

                {/* Live Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Results</p>
                    <div className="space-y-1 max-h-44 overflow-y-auto">
                      {searchResults.map((result) => {
                        const addr = result.address;
                        const primaryName =
                          addr.city || addr.town || addr.municipality || addr.district || addr.county ||
                          addr.village || result.display_name.split(',')[0];
                        const stateName = addr.state || '';
                        return (
                          <button
                            key={result.place_id}
                            onClick={() => handleSelectResult(result)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-xl hover:bg-blue-50 transition-colors group"
                          >
                            <MapPin size={13} className="text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-slate-800 font-medium group-hover:text-blue-700">{primaryName}</p>
                              {stateName && <p className="text-[10px] text-slate-500">{stateName}</p>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Suggested cities (shown when not searching) */}
                {!searchQuery && (
                  <div className="space-y-2">
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Suggested</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {suggestedCities.map((city) => (
                        <button
                          key={city}
                          onClick={() => {
                            setLocationName(city);
                            if (onLocationChange) onLocationChange(city);
                            setIsDropdownOpen(false);
                          }}
                          className={`px-2 py-1.5 text-xs rounded-lg text-left transition-all border ${
                            locationName === city
                              ? 'bg-blue-600 text-white border-blue-500 font-semibold'
                              : 'text-slate-700 hover:text-blue-600 hover:bg-blue-50 border-transparent hover:border-blue-200'
                          }`}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-3 flex items-center gap-2 border-t border-blue-100">
                <Navigation size={12} className="text-blue-600" />
                <span className="text-[10px] text-blue-700 font-medium">Automatic detection enabled</span>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LocationSelector;
